import {useCallback, useEffect, useState} from "react";
import {isInFileMaker} from "@/utils/fmScripts.ts";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

let warned = false;

const warnOnce = (message: string) => {
    if (!warned) {
        warned = true;
        console.warn(message);
    }
};

const isVersion = (value: unknown): value is {version: string} => {
    return (
        typeof value === "object" &&
        value !== null &&
        "version" in value &&
        typeof value.version === "string" &&
        value.version !== ""
    );
};

const fetchDeployedVersion = async (): Promise<string | null> => {
    const response = await fetch(`/version.json?t=${Date.now()}`, {cache: "no-store"});

    if (!response.ok) {
        return null;
    }

    // the SPA rewrite answers a missing file with index.html and a 200
    if (response.headers.get("content-type")?.includes("text/html")) {
        warnOnce("version.json is missing from this deployment, update prompts are disabled");
        return null;
    }

    let body: unknown;

    try {
        body = await response.json();
    } catch {
        warnOnce("version.json could not be parsed, update prompts are disabled");
        return null;
    }

    if (!isVersion(body)) {
        warnOnce("version.json is not in the expected shape, update prompts are disabled");
        return null;
    }

    return body.version;
};

export const useNewReleaseAvailable = () => {
    const [available, setAvailable] = useState<boolean>(false);

    const check = useCallback(async () => {
        try {
            // a reload inside a FileMaker WebViewer loses the injected session
            if (await isInFileMaker) {
                return;
            }

            const deployed = await fetchDeployedVersion();

            if (deployed && deployed !== __BUILD_VERSION__) {
                setAvailable(true);
            }
        } catch {
            // offline or a transient error, so leave the prompt alone
        }
    }, []);

    useEffect(() => {
        const onFocus = () => {
            void check();
        };
        const interval = setInterval(onFocus, CHECK_INTERVAL_MS);
        window.addEventListener("focus", onFocus);

        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", onFocus);
        };
    }, [check]);

    return {available, dismiss: () => setAvailable(false)};
};
